import { Common, color } from '@stockbit/ui';
import { AVATAR_URL } from 'constants/api';
import { LabelAdmin, LabelTopUser, LabelVerifiedAccount } from 'global/Labels';
import { handleErrorAvatar } from 'utils/general';
import { useState } from 'react';
import { ModalFollower, ModalFollowing } from 'global/Moderation/Modals';
import CloseSvg from 'global/Icons/CloseSvg';
import Link from 'next/link';
import Image from 'global/Image';
import auth from 'utils/auth';
import { useDispatch, useSelector } from 'react-redux';
import { effects } from 'global/Moderation/slice';
import moment from 'moment';
import { profileSelector } from 'lib/entities/profile';
import ProfileStatsAttribute from './ProfileStatsAttribute';
import {
  ProfileStats,
  ProfileStatsContainer,
  ProfileAvatar,
  ProfileUsernameContainer,
  ModaAvatar,
} from '../styled';
import TopHeader from './TopHeader';
import { UserGraph } from '../../../../@types/user';
import FollowedBy from './FollowedBy';

const { Flex, Text } = Common;

interface BodyHeaderProp {
  // eslint-disable-next-line no-undef
  user: UserGraph;
}

interface ProfileStatType {
  label: string;
  value: string | number;
  onClick: () => void;
}

function BodyHeader({ user }: BodyHeaderProp) {
  // @ts-ignore
  const { created } = useSelector(profileSelector);

  const { profile, statistic, badge } = user;
  const {
    avatar_urls: { default_url: avatar },
    username,
    about,
    fullname,
    website_url: website,
  } = profile;
  const {
    follower_count: followerCount,
    idea_count: ideaCount,
    following_count: followingCount,
  } = statistic;

  const { is_verified: isVerified, is_admin: isAdmin, is_top: isTop } = badge;
  const avatarUrl = avatar.startsWith('http')
    ? avatar
    : `${AVATAR_URL}/${avatar || ''}`;

  const [modalFollower, showModalFollower] = useState<boolean>(false);
  const [modalFollowing, showModalFollowing] = useState<boolean>(false);
  const [modalProfilePicture, showModalProfilePicture] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const handleCloseModalFollower = () => {
    showModalFollower(false);
    dispatch(
      effects.getMutualFollower({
        userid: user.user_id,
        page: 1,
        limit: 10,
      }),
    );
  };

  const handleCloseModalFollowing = () => {
    showModalFollowing(false);
    dispatch(
      effects.getMutualFollower({
        userid: user.user_id,
        page: 1,
        limit: 10,
      }),
    );
  };

  const stats: Array<ProfileStatType> = [
    {
      label: 'Posts',
      value: ideaCount || 0,
      onClick: () => {},
    },
    {
      label: 'Followers',
      value: followerCount || 0,
      onClick: () => showModalFollower(true),
    },
    {
      label: 'Following',
      value: followingCount || 0,
      onClick: () => showModalFollowing(true),
    },
  ];

  const isCurrentProfile =
    // @ts-ignore
    user.profile.username === auth.getUserAccess()?.username;
  const joined = moment(isCurrentProfile ? created : user.created_at).format(
    'MMMM YYYY',
  );

  return (
    <>
      <Flex align="flex-start">
        <ProfileAvatar onClick={() => showModalProfilePicture(true)}>
          <Image
            src={avatarUrl}
            alt={`${username}'s avatar`}
            onError={(e) => handleErrorAvatar(e, username)}
          />
        </ProfileAvatar>
        <Flex width="100%" direction="column" padding="0 0 0 20px">
          <Flex width="100%" direction="column">
            <ProfileUsernameContainer>
              <Flex align="center">
                <Text as="h1" size="20px" family="bold" margin="0 0 2px 0">
                  {username}
                </Text>
                <LabelVerifiedAccount
                  deprecatedVerifiedStatus={isVerified}
                  verifiedStatus={user.badge?.verified_status}
                />
                {isTop && <LabelTopUser />}
                {isAdmin && <LabelAdmin privilege={1} />}
              </Flex>
              <TopHeader user={user} username={username} />
            </ProfileUsernameContainer>
            <Text mt="-4px" as="h2" size="16px" height="20px">
              {fullname.substring(0, 30)}
              {fullname.length > 30 ? '...' : ''}
            </Text>
          </Flex>

          <ProfileStatsContainer>
            {stats.map(({ label, value, onClick }) => (
              <ProfileStats role="button" onClick={onClick}>
                <ProfileStatsAttribute value={value} label={label} />
              </ProfileStats>
            ))}
          </ProfileStatsContainer>
          <Flex direction="column" mb="16px">
            <Text size="14px" mb="2px">
              {about}
            </Text>
            {website && (
              <Link href={website}>
                <a target="_blank" rel="noreferrer noopener">
                  <Text color={color.primary} width="200px" ellipsis>
                    {website}
                  </Text>
                </a>
              </Link>
            )}
            {!isCurrentProfile && (
              <FollowedBy userId={user.user_id} username={username} />
            )}
          </Flex>
          <Flex>
            <Image src="/icon/calendar.svg" alt="calendar" py="2px" />
            <Text color={color.highgray} ml="6px">
              Joined {joined}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <ModalFollower
        username={username}
        userId={user.user_id}
        visible={modalFollower}
        onCancel={handleCloseModalFollower}
      />
      <ModalFollowing
        username={username}
        userId={user.user_id}
        visible={modalFollowing}
        onCancel={handleCloseModalFollowing}
      />
      <ModaAvatar
        visible={modalProfilePicture}
        onCancel={() => showModalProfilePicture(false)}
        width={310}
        closeIcon={null}
        centered
      >
        <ProfileAvatar>
          <Image
            src={avatarUrl}
            alt={`${username}'s avatar`}
            onError={(e) => handleErrorAvatar(e, username)}
          />
        </ProfileAvatar>
      </ModaAvatar>
    </>
  );
}

export default BodyHeader;
